import { initAxios } from '../../../utils/axiosHelper';
import { IComment, ICreateComment, IUpdateComment } from './comment.d';

// Create a new comment for an entity or product
export const createComment = async(
  commentData: ICreateComment,
): Promise<IComment | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.post(
      '/bite-db/comment',
      JSON.stringify(commentData),
    );
    return resp.data?.data || null;
  } catch (err) {
    return null;
  }
};

// Get a comment by ID
export const getCommentById = async(id?: string): Promise<IComment | null> => {
  try {
    if (!id) {
      return null;
    }
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/comment/${id}`);
    return resp.data?.data || null;
  } catch (err) {
    return null;
  }
};

// Get comments for an entity or product
export const getCommentsByCommentableId = async(
  commentableId?: string,
  commentableType?: 'DatabaseEntity' | 'DatabaseProduct',
): Promise<IComment[]> => {
  try {
    if (!commentableId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const queryParams = commentableType ?
      `?commentableType=${commentableType}` :
      '';
    const resp = await instance.get(
      `/bite-db/comments/${commentableId}${queryParams}`,
    );
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};

// Get comments with nested replies for an entity or product
export const getCommentsWithReplies = async(
  commentableId?: string,
  commentableType?: 'DatabaseEntity' | 'DatabaseProduct',
): Promise<IComment[]> => {
  try {
    if (!commentableId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const queryParams = commentableType ?
      `?commentableType=${commentableType}` :
      '';
    const resp = await instance.get(
      `/bite-db/comments/${commentableId}/with-replies${queryParams}`,
    );
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};

// Update a comment
export const updateComment = async(
  id: string,
  updateData: IUpdateComment,
): Promise<IComment | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.put(
      `/bite-db/comment/${id}`,
      JSON.stringify(updateData),
    );
    return resp.data?.data || null;
  } catch (err) {
    return null;
  }
};

// Delete a comment (soft delete)
export const deleteComment = async(id: string): Promise<boolean> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return false;
    }

    const resp = await instance.delete(`/bite-db/comment/${id}`);
    return resp.data?.success || false;
  } catch (err) {
    return false;
  }
};

// Get replies to a specific comment
export const getCommentReplies = async(
  parentId?: string,
): Promise<IComment[]> => {
  try {
    if (!parentId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/bite-db/comments/replies/${parentId}`);
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};

// Client-side filtering function for comments
export const filterComments = (
  comments: IComment[],
  searchTerm: string,
): IComment[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return comments;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return comments.filter((comment) => {
    const content = comment.content?.toLowerCase() || '';
    const authorName = comment.author_name?.toLowerCase() || '';

    return (
      content.includes(lowerSearchTerm) || authorName.includes(lowerSearchTerm)
    );
  });
};
